import {useCallback} from 'react';
import usePaidGigDetailQuery from '@/hooks/queries/gigs/use-paid-gig-detail-query';
import useGigSubmissionsQuery from '@/hooks/queries/content-submissions/use-gig-submissions-query';
import useDeliverableSlots from '../useDeliverableSlots';
import {
  buildPostRequirements,
  combineDeliverableTotals,
} from '../postRequirements';

/**
 * The sidebar's progress bar counts approved uploads, so every tab needs the gig's
 * submissions as well as the gig. Both queries are cached per gig, so switching tabs
 * costs no extra requests.
 */
export default function useGigShellData(gigId: string) {
  const {
    deliverables,
    uploadCounts,
    shell,
    brief,
    application,
    prep,
    affiliate,
    isLoading: gigLoading,
    isError: gigFailed,
    error: gigError,
    refetch: refetchGig,
  } = usePaidGigDetailQuery(gigId);

  const {
    submissions,
    isLoading: submissionsLoading,
    isError: submissionsFailed,
    error: submissionsError,
    refetch: refetchSubmissions,
  } = useGigSubmissionsQuery(gigId);

  const slots = useDeliverableSlots({deliverables, submissions, uploadCounts});
  const posts = buildPostRequirements(shell, gigId);
  const totals = combineDeliverableTotals(
    {
      totalRequired: slots.totalRequired,
      totalApproved: slots.totalApproved,
      needsActionCount: slots.needsActionCount,
    },
    posts
  );

  const refetch = useCallback(() => {
    refetchGig();
    refetchSubmissions();
  }, [refetchGig, refetchSubmissions]);

  return {
    shell,
    brief,
    application,
    prep,
    affiliate,
    deliverables,
    posts,
    slots,
    totals,
    // Combined for the deliverables page, which cannot render without submissions.
    // The Information tabs read neither, so they gate on the gig flags alone and let
    // the progress bar drop out rather than taking the page down with it.
    isLoading: gigLoading || submissionsLoading,
    isError: gigFailed || submissionsFailed,
    error: gigError ?? submissionsError,
    gigLoading,
    gigFailed,
    submissionsLoading,
    submissionsFailed,
    refetch,
  };
}
